import React, { FC, MouseEvent, useMemo } from 'react';
import { ReadonlySignal, RouterLocation } from '@funtech-int/rival';
import { TvChannel, TvChannelProgram } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { createPictureFactory, Picture, PictureData } from '@funtech-int/yango-shared';
import { useSignal } from '@funtech-int/rival-react';

import * as CardKit from '../CardKit';
import { useCurrentProgram } from '../../hooks/useCurrentProgram';

import styles from './_ChannelProgram.module.css';

const coverFactory = createPictureFactory({
    fallback: {
        srcSet: {
            '1x': '335x223',
            '2x': '502x335',
        },
        src: '335x223',
    },
});

const logoFactory = createPictureFactory({
    fallback: {
        srcSet: {
            '1x': '96x96',
            '2x': '144x144',
        },
        src: '96x96',
    },
});

interface ChannelProgramProps {
    channel: TvChannel;
    location: RouterLocation<any, any>;
    timeSignal: ReadonlySignal<number>;
    startContent: (contentId: string) => void;
    onShow?: (program?: TvChannelProgram) => void;
    onClick?: (program?: TvChannelProgram) => void;
}

export const ChannelProgram: FC<ChannelProgramProps> = ({
    channel,
    location,
    timeSignal,
    startContent,
    onShow,
    onClick,
}) => {
    const now = useSignal(timeSignal);

    const controller = useMemo(() => {
        let program: TvChannelProgram | undefined;

        const searchParams = new URLSearchParams({
            openedChannelId: channel.contentId,
        });

        return {
            searchParams,

            setProgram(value?: TvChannelProgram) {
                program = value;
            },

            onClick(e: MouseEvent<HTMLAnchorElement>) {
                onClick?.(program);
                if (channel.viewOption?.isPurchased) {
                    e.preventDefault();
                    startContent(channel.contentId);
                }
            },
            onShow() {
                onShow?.(program);
            },
        };
    }, [channel, onClick, onShow, startContent]);

    const { img: logo } = logoFactory({
        fallbackUrl: channel.logo?.toString(),
    });

    const { program, time, announce } = useCurrentProgram(channel, now);

    let cover: PictureData | undefined;

    if (program) {
        cover = coverFactory({
            fallbackUrl: program.image?.toString(),
        });
    } else {
        cover = coverFactory({
            fallbackUrl: channel.horizontalCover?.toString(),
        });
    }

    controller.setProgram(program ?? undefined);

    return (
        <CardKit.Layout
            location={location}
            searchParams={controller.searchParams}
            dataTid="CardChannelProgram"
            onClick={controller.onClick}
            onShow={controller.onShow}
        >
            <CardKit.ImageOverlay
                image={
                    <div className={styles.imageContainer}>
                        {cover?.img.src ? (
                            <Picture
                                loading="lazy"
                                srcSet={cover.img.srcSet}
                                src={cover.img.src}
                                className={styles.image}
                                data-tid="AdaptiveImage"
                            />
                        ) : null}
                    </div>
                }
            >
                <CardKit.Play variant={channel.viewOption?.isPurchased ? 'primary' : 'plus'} />
                {program ? (
                    <CardKit.Progress
                        value={program.elapsed(now)}
                        maxValue={!announce ? program.duration : 0}
                        dateTime={time}
                        text={time}
                    />
                ) : null}
            </CardKit.ImageOverlay>
            <figcaption className={styles.meta}>
                <Picture
                    loading="lazy"
                    srcSet={logo.srcSet}
                    src={logo.src}
                    className={styles.logo}
                    data-tid="AdaptiveImage"
                />
                <div className={styles.text}>
                    <p className={styles.description}>{channel.title}</p>
                    {program?.title ? <p className={styles.title}>{program.title}</p> : null}
                </div>
            </figcaption>
        </CardKit.Layout>
    );
};
